// Part of salesPages, split by screen to keep files short; ../salesPages.ts merges the parts.
// Keys sit directly under salesPages, in sub-objects whose names no other part uses.
// The sales document editor (components/sales/document-form.tsx); the bill form's shared words
// live in purchasePages.form and are reused from there.
export const en = {
  documentForm: {
    // Sentences that name the document type, one set per kind (Arabic agrees with the noun).
    kinds: {
      invoice: {
        deleteConfirm: 'Delete this invoice? This cannot be undone.',
        voidNotice:
          'This invoice is void. Its posting was reversed; the number stays in the sequence.',
        voidNoticeReason:
          'This invoice is void: {{reason}}. Its posting was reversed; the number stays in the sequence.',
        editPosted: 'Saving changes reverses the original posting and posts the edited invoice.',
        voidTitle: 'Void invoice {{number}}',
        messageOn: 'Message on invoice',
      },
      credit_note: {
        deleteConfirm: 'Delete this credit note? This cannot be undone.',
        voidNotice:
          'This credit note is void. Its posting was reversed; the number stays in the sequence.',
        voidNoticeReason:
          'This credit note is void: {{reason}}. Its posting was reversed; the number stays in the sequence.',
        editPosted:
          'Saving changes reverses the original posting and posts the edited credit note.',
        voidTitle: 'Void credit note {{number}}',
        messageOn: 'Message on credit note',
      },
      sales_receipt: {
        deleteConfirm: 'Delete this sales receipt? This cannot be undone.',
        voidNotice:
          'This sales receipt is void. Its posting was reversed; the number stays in the sequence.',
        voidNoticeReason:
          'This sales receipt is void: {{reason}}. Its posting was reversed; the number stays in the sequence.',
        editPosted:
          'Saving changes reverses the original posting and posts the edited sales receipt.',
        voidTitle: 'Void sales receipt {{number}}',
        messageOn: 'Message on sales receipt',
      },
      quote: {
        deleteConfirm: 'Delete this quote? This cannot be undone.',
        voidNotice:
          'This quote is void. Its posting was reversed; the number stays in the sequence.',
        voidNoticeReason:
          'This quote is void: {{reason}}. Its posting was reversed; the number stays in the sequence.',
        editPosted: 'Saving changes reverses the original posting and posts the edited quote.',
        voidTitle: 'Void quote {{number}}',
        messageOn: 'Message on quote',
      },
      sales_order: {
        deleteConfirm: 'Delete this sales order? This cannot be undone.',
        voidNotice:
          'This sales order is void. Its posting was reversed; the number stays in the sequence.',
        voidNoticeReason:
          'This sales order is void: {{reason}}. Its posting was reversed; the number stays in the sequence.',
        editPosted:
          'Saving changes reverses the original posting and posts the edited sales order.',
        voidTitle: 'Void sales order {{number}}',
        messageOn: 'Message on sales order',
      },
      delivery_challan: {
        deleteConfirm: 'Delete this delivery challan? This cannot be undone.',
        voidNotice: 'This delivery challan is cancelled. Its number stays in the series.',
        voidNoticeReason:
          'This delivery challan is cancelled: {{reason}}. Its number stays in the series.',
        editPosted:
          'Saving changes reverses the original posting and posts the edited delivery challan.',
        voidTitle: 'Cancel delivery challan {{number}}',
        messageOn: 'Message on delivery challan',
      },
    },
    // Sales orders and delivery challans (document 06 S13, S14).
    orderKinds: {
      sales_order: {
        close: 'Close sales order',
        closedNotice: 'This sales order is closed. Reopen it from More to change or invoice it.',
        fulfilledNotice:
          'Lines of this sales order have been invoiced or delivered, so it can no longer be edited; close it when nothing more is expected.',
        linesOpen:
          'Lines still open on sales order {{number}}; each stays linked to its line, and saving updates the sales order.',
      },
      delivery_challan: {
        close: 'Close delivery challan',
        closedNotice:
          'This delivery challan is closed. Reopen it from More to change or invoice it.',
        fulfilledNotice:
          'Lines of this delivery challan have been invoiced, so it can no longer be edited; close it when nothing more is expected.',
        linesOpen:
          'Lines still open on delivery challan {{number}}; each stays linked to its line, and saving updates the delivery challan.',
      },
    },
    irnLockedInvoice:
      'This invoice has an IRN, so it cannot be edited or voided. Cancel the IRN within 24 hours of registration, or issue a credit note.',
    irnLockedCreditNote:
      'This credit note has an IRN, so it cannot be edited or voided. Cancel the IRN within 24 hours of registration, or issue a credit note.',
    ewbLockedInvoice:
      'This invoice has an active e-way bill, so it cannot be edited or voided. Cancel the e-way bill first.',
    ewbLockedCreditNote:
      'This credit note has an active e-way bill, so it cannot be edited or voided. Cancel the e-way bill first.',
    // Toasts
    cancelled: '{{doc}} cancelled.',
    closed: '{{doc}} closed.',
    reopened: '{{doc}} reopened.',
    quoteMarked: {
      pending: 'Quote {{number}} marked pending.',
      accepted: 'Quote {{number}} marked accepted.',
      declined: 'Quote {{number}} marked declined.',
    },
    markAs: {
      pending: 'Mark as pending',
      accepted: 'Mark as accepted',
      declined: 'Mark as declined',
    },
    // Validation: the date that may not come before the document date
    expirationDateLower: 'expiration date',
    shipmentDateLower: 'expected shipment date',
    // Footer and menus
    einvoicePrefix: 'E-invoice:',
    convert: 'Convert',
    convertToSalesOrder: 'Convert to sales order',
    createDeliveryChallan: 'Create delivery challan',
    applyToInvoices: 'Apply to invoices',
    previewPdf: 'Preview PDF',
    downloadPdf: 'Download PDF',
    saveFirst: 'Save the document first',
    cancelChallan: 'Cancel challan',
    approveAndSend: 'Approve and send',
    // Notices
    alreadyPaid: '{{amount}} is already paid or credited.',
    sentOn: 'Sent on {{date}}.',
    quoteConverted: 'This quote was converted to an invoice.',
    staffOnly: 'Only your accounting team can change sales documents.',
    clientReview:
      'Your accountant reviews this invoice and approves it before it is posted and numbered.',
    // Header
    customerEmailPlaceholder: 'Separate several with commas',
    noAddress: 'No address on the customer.',
    challanType: 'Challan type *',
    challanTypePlaceholder: 'Why the goods move',
    expirationDate: 'Expiration date',
    expectedShipmentDate: 'Expected shipment date',
    dateOfSupplyHint: 'If different from the document date',
    placeOfSupplyHintIndia:
      'CGST + SGST within the state of registration; IGST to other states and abroad.',
    outsideUaeNotSet: 'Outside the UAE / not set',
    depositTo: 'Deposit to *',
    appliesToInvoice: 'Applies to invoice',
    reasonForCredit: 'Reason for credit *',
    reasonForCreditPlaceholder: 'Shown on the tax credit note',
    // Line grid
    lines: '{{kind}} lines',
    lineN: 'line {{n}}',
    invoiced: 'Invoiced',
    delivered: 'Delivered',
    hsnSacLine: 'HSN/SAC, {{line}}',
    // Memo, links and totals
    messageOnStatement: 'Message on statement',
    from: 'From',
    invoicesAndChallans: 'Invoices and challans',
    invoices: 'Invoices',
    voidSuffix: '(void)',
    draftSuffix: '(draft)',
    convertedFromQuote: 'Converted from quote',
    originalInvoice: 'Original invoice',
    shortcutsPosted:
      'Shortcuts: Ctrl+Alt+S save · Ctrl+Alt+D approve and new · Ctrl+Alt+M send · Ctrl+Alt+X close. Amounts accept arithmetic such as 1200*1.05.',
    shortcutsApprove:
      'Shortcuts: Ctrl+Alt+S approve and close · Ctrl+Alt+D approve and new · Ctrl+Alt+M send · Ctrl+Alt+X close. Amounts accept arithmetic such as 1200*1.05.',
    shortcutsSaved:
      'Shortcuts: Ctrl+Alt+S save and close · Ctrl+Alt+D save and new · Ctrl+Alt+M send · Ctrl+Alt+X close. Amounts accept arithmetic such as 1200*1.05.',
    shortcutsNew:
      'Shortcuts: Ctrl+Alt+S save and close · Ctrl+Alt+D save and new · Ctrl+Alt+X close. Amounts accept arithmetic such as 1200*1.05.',
    voidChallanDescription:
      'The challan stays in the series as cancelled (GSTR-1 table 13 counts it). This cannot be undone.',
  },
};

export const ar = {
  documentForm: {
    kinds: {
      invoice: {
        deleteConfirm: 'هل تريد حذف هذه الفاتورة؟ لا يمكن التراجع عن ذلك.',
        voidNotice: 'هذه الفاتورة ملغاة. عُكس ترحيلها، ويبقى رقمها في التسلسل.',
        voidNoticeReason: 'هذه الفاتورة ملغاة: {{reason}}. عُكس ترحيلها، ويبقى رقمها في التسلسل.',
        editPosted: 'يؤدي حفظ التغييرات إلى عكس الترحيل الأصلي وترحيل الفاتورة بعد تعديلها.',
        voidTitle: 'إلغاء الفاتورة {{number}}',
        messageOn: 'رسالة على الفاتورة',
      },
      credit_note: {
        deleteConfirm: 'هل تريد حذف هذا الإشعار الدائن؟ لا يمكن التراجع عن ذلك.',
        voidNotice: 'هذا الإشعار الدائن ملغى. عُكس ترحيله، ويبقى رقمه في التسلسل.',
        voidNoticeReason:
          'هذا الإشعار الدائن ملغى: {{reason}}. عُكس ترحيله، ويبقى رقمه في التسلسل.',
        editPosted: 'يؤدي حفظ التغييرات إلى عكس الترحيل الأصلي وترحيل الإشعار الدائن بعد تعديله.',
        voidTitle: 'إلغاء الإشعار الدائن {{number}}',
        messageOn: 'رسالة على الإشعار الدائن',
      },
      sales_receipt: {
        deleteConfirm: 'هل تريد حذف إيصال المبيعات هذا؟ لا يمكن التراجع عن ذلك.',
        voidNotice: 'إيصال المبيعات هذا ملغى. عُكس ترحيله، ويبقى رقمه في التسلسل.',
        voidNoticeReason:
          'إيصال المبيعات هذا ملغى: {{reason}}. عُكس ترحيله، ويبقى رقمه في التسلسل.',
        editPosted: 'يؤدي حفظ التغييرات إلى عكس الترحيل الأصلي وترحيل إيصال المبيعات بعد تعديله.',
        voidTitle: 'إلغاء إيصال المبيعات {{number}}',
        messageOn: 'رسالة على إيصال المبيعات',
      },
      quote: {
        deleteConfirm: 'هل تريد حذف عرض السعر هذا؟ لا يمكن التراجع عن ذلك.',
        voidNotice: 'عرض السعر هذا ملغى. عُكس ترحيله، ويبقى رقمه في التسلسل.',
        voidNoticeReason: 'عرض السعر هذا ملغى: {{reason}}. عُكس ترحيله، ويبقى رقمه في التسلسل.',
        editPosted: 'يؤدي حفظ التغييرات إلى عكس الترحيل الأصلي وترحيل عرض السعر بعد تعديله.',
        voidTitle: 'إلغاء عرض السعر {{number}}',
        messageOn: 'رسالة على عرض السعر',
      },
      sales_order: {
        deleteConfirm: 'هل تريد حذف أمر البيع هذا؟ لا يمكن التراجع عن ذلك.',
        voidNotice: 'أمر البيع هذا ملغى. عُكس ترحيله، ويبقى رقمه في التسلسل.',
        voidNoticeReason: 'أمر البيع هذا ملغى: {{reason}}. عُكس ترحيله، ويبقى رقمه في التسلسل.',
        editPosted: 'يؤدي حفظ التغييرات إلى عكس الترحيل الأصلي وترحيل أمر البيع بعد تعديله.',
        voidTitle: 'إلغاء أمر البيع {{number}}',
        messageOn: 'رسالة على أمر البيع',
      },
      delivery_challan: {
        deleteConfirm: 'هل تريد حذف إذن التسليم هذا؟ لا يمكن التراجع عن ذلك.',
        voidNotice: 'إذن التسليم هذا ملغى. يبقى رقمه في السلسلة.',
        voidNoticeReason: 'إذن التسليم هذا ملغى: {{reason}}. يبقى رقمه في السلسلة.',
        editPosted: 'يؤدي حفظ التغييرات إلى عكس الترحيل الأصلي وترحيل إذن التسليم بعد تعديله.',
        voidTitle: 'إلغاء إذن التسليم {{number}}',
        messageOn: 'رسالة على إذن التسليم',
      },
    },
    orderKinds: {
      sales_order: {
        close: 'إقفال أمر البيع',
        closedNotice: 'أمر البيع هذا مُقفل. أعد فتحه من «المزيد» لتغييره أو إصدار فاتورة به.',
        fulfilledNotice:
          'صدرت فواتير ببعض أسطر أمر البيع هذا أو سُلّمت، لذا لم يعد من الممكن تعديله؛ أقفله عندما لا يُتوقع المزيد.',
        linesOpen:
          'الأسطر التي لا تزال مفتوحة في أمر البيع {{number}}؛ يبقى كل منها مرتبطاً بسطره، ويؤدي الحفظ إلى تحديث أمر البيع.',
      },
      delivery_challan: {
        close: 'إقفال إذن التسليم',
        closedNotice: 'إذن التسليم هذا مُقفل. أعد فتحه من «المزيد» لتغييره أو إصدار فاتورة به.',
        fulfilledNotice:
          'صدرت فواتير ببعض أسطر إذن التسليم هذا، لذا لم يعد من الممكن تعديله؛ أقفله عندما لا يُتوقع المزيد.',
        linesOpen:
          'الأسطر التي لا تزال مفتوحة في إذن التسليم {{number}}؛ يبقى كل منها مرتبطاً بسطره، ويؤدي الحفظ إلى تحديث إذن التسليم.',
      },
    },
    irnLockedInvoice:
      'لهذه الفاتورة IRN، لذا لا يمكن تعديلها أو إلغاؤها. ألغِ IRN خلال 24 ساعة من تسجيله، أو أصدر إشعاراً دائناً.',
    irnLockedCreditNote:
      'لهذا الإشعار الدائن IRN، لذا لا يمكن تعديله أو إلغاؤه. ألغِ IRN خلال 24 ساعة من تسجيله، أو أصدر إشعاراً دائناً.',
    ewbLockedInvoice:
      'لهذه الفاتورة بوليصة e-way نشطة، لذا لا يمكن تعديلها أو إلغاؤها. ألغِ بوليصة e-way أولاً.',
    ewbLockedCreditNote:
      'لهذا الإشعار الدائن بوليصة e-way نشطة، لذا لا يمكن تعديله أو إلغاؤه. ألغِ بوليصة e-way أولاً.',
    cancelled: 'تم إلغاء {{doc}}.',
    closed: 'تم إقفال {{doc}}.',
    reopened: 'تمت إعادة فتح {{doc}}.',
    quoteMarked: {
      pending: 'عُلّم عرض السعر {{number}} بأنه قيد الانتظار.',
      accepted: 'عُلّم عرض السعر {{number}} بأنه مقبول.',
      declined: 'عُلّم عرض السعر {{number}} بأنه مرفوض.',
    },
    markAs: {
      pending: 'تعليم كقيد الانتظار',
      accepted: 'تعليم كمقبول',
      declined: 'تعليم كمرفوض',
    },
    expirationDateLower: 'تاريخ انتهاء الصلاحية',
    shipmentDateLower: 'تاريخ الشحن المتوقع',
    einvoicePrefix: 'الفاتورة الإلكترونية:',
    convert: 'تحويل',
    convertToSalesOrder: 'تحويل إلى أمر بيع',
    createDeliveryChallan: 'إنشاء إذن تسليم',
    applyToInvoices: 'التطبيق على الفواتير',
    previewPdf: 'معاينة PDF',
    downloadPdf: 'تنزيل PDF',
    saveFirst: 'احفظ المستند أولاً',
    cancelChallan: 'إلغاء إذن التسليم',
    approveAndSend: 'اعتماد وإرسال',
    alreadyPaid: 'دُفع أو أُضيف رصيد دائن بمبلغ {{amount}} مسبقاً.',
    sentOn: 'أُرسل في {{date}}.',
    quoteConverted: 'حُوّل عرض السعر هذا إلى فاتورة.',
    staffOnly: 'لا يمكن تغيير مستندات المبيعات إلا لفريق المحاسبة لديك.',
    clientReview: 'يراجع محاسبك هذه الفاتورة ويعتمدها قبل ترحيلها وترقيمها.',
    customerEmailPlaceholder: 'افصل بين العناوين بفواصل',
    noAddress: 'لا يوجد عنوان للعميل.',
    challanType: 'نوع إذن التسليم *',
    challanTypePlaceholder: 'سبب نقل البضائع',
    expirationDate: 'تاريخ انتهاء الصلاحية',
    expectedShipmentDate: 'تاريخ الشحن المتوقع',
    dateOfSupplyHint: 'إذا اختلف عن تاريخ المستند',
    placeOfSupplyHintIndia:
      'CGST + SGST داخل ولاية التسجيل؛ وIGST إلى الولايات الأخرى وخارج البلاد.',
    outsideUaeNotSet: 'خارج الإمارات / غير محدد',
    depositTo: 'الإيداع في *',
    appliesToInvoice: 'ينطبق على الفاتورة',
    reasonForCredit: 'سبب الإشعار الدائن *',
    reasonForCreditPlaceholder: 'يظهر على الإشعار الدائن الضريبي',
    lines: 'أسطر {{kind}}',
    lineN: 'السطر {{n}}',
    invoiced: 'المفوتر',
    delivered: 'المسلَّم',
    hsnSacLine: 'HSN/SAC، {{line}}',
    messageOnStatement: 'رسالة على كشف الحساب',
    from: 'من',
    invoicesAndChallans: 'الفواتير وأذونات التسليم',
    invoices: 'الفواتير',
    voidSuffix: '(ملغى)',
    draftSuffix: '(مسودة)',
    convertedFromQuote: 'محوّل من عرض السعر',
    originalInvoice: 'الفاتورة الأصلية',
    shortcutsPosted:
      'الاختصارات: Ctrl+Alt+S حفظ · Ctrl+Alt+D اعتماد وجديد · Ctrl+Alt+M إرسال · Ctrl+Alt+X إغلاق. تقبل المبالغ العمليات الحسابية مثل 1200*1.05.',
    shortcutsApprove:
      'الاختصارات: Ctrl+Alt+S اعتماد وإغلاق · Ctrl+Alt+D اعتماد وجديد · Ctrl+Alt+M إرسال · Ctrl+Alt+X إغلاق. تقبل المبالغ العمليات الحسابية مثل 1200*1.05.',
    shortcutsSaved:
      'الاختصارات: Ctrl+Alt+S حفظ وإغلاق · Ctrl+Alt+D حفظ وجديد · Ctrl+Alt+M إرسال · Ctrl+Alt+X إغلاق. تقبل المبالغ العمليات الحسابية مثل 1200*1.05.',
    shortcutsNew:
      'الاختصارات: Ctrl+Alt+S حفظ وإغلاق · Ctrl+Alt+D حفظ وجديد · Ctrl+Alt+X إغلاق. تقبل المبالغ العمليات الحسابية مثل 1200*1.05.',
    voidChallanDescription:
      'يبقى إذن التسليم في السلسلة بحالة ملغى (يحتسبه الجدول 13 من GSTR-1). لا يمكن التراجع عن ذلك.',
  },
};
